/* eslint-disable max-len */
import { Arr } from '@deqo/foundation';
import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import { useDraft } from '../../../CodesThatsGottaGoSomewhereElse';
import { ApiClient } from '../../../api/ApiClient';
import { v2 } from '../../../api/ApiInterfaceV2';
import { BoardLiteData } from '../../../api/ApiTypes';
import { TemplateData } from '../../../api/endpoints/templates';
import { Icon } from '../../../concepts/resources/Icon';
import { useLoader, useSubmitter } from '../../../platform/Hook';
import { Button, Colors, Input, Select, usePrompt } from '../../../ui';
import { DropButton } from '../../../ui/DropButton';
import { LoadingGuard } from '../../../ui/LoadingGuard';
import { useResolve } from '../../Container';
import { ProfileModel } from '../../ProfileModel';
import { ButtonStrip, Copy, Flexer, Spacer, Title } from '../../dashboard/elements/LayoutElements';
import { Popup, PopupProps } from '../../popups/Popup';
import { BoardInfo } from '../BoardInfo';
import { useEditorSessionObserver } from '../EditorSession';

const adjs = ['beautiful', 'cool', 'crazy', 'dazzling', 'fabulous', 'gorgeous', 'spicy', 'swanky', 'tremendous'];

const Frame = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1rem;
    width: 32rem;

    ${DropButton} {
        cursor: pointer;
    }

    ${Input.Label} {
        color: ${Colors.text};
    }
`;

type OnComplete = (templateData?: BoardLiteData | TemplateData) => void;

const PublishNewView = ({ onComplete }: { onComplete: OnComplete }) => {

    const api = useResolve(ApiClient);
    const source = useEditorSessionObserver(BoardInfo);
    const me = useResolve(ProfileModel);

    const [draft, patchDraft] = useDraft({
        label: source.label,
        desc: `A ${Arr.random(adjs)} Ludi template by ${me.name}.`,
        group: null,
        time: '',
        complexity: '',
        people: '',
    });

    const [errors, setErrors] = useState({
        any: false,
        label: false,
        desc: false,
        group: false,
    });

    const [loading, templates] = useLoader(() => api.exec(v2.templates.list()), []);

    const draftValidate = useCallback(() => {
        const label = !draft.label.trim();
        const desc = !draft.desc.trim();
        const group = !draft.group;
        setErrors({
            any: label || desc || group,
            label, desc, group,
        });
        return !label && !desc && !group;
    }, [draft]);

    const [onPublish, working] = useSubmitter(async () => {
        if (!draftValidate()) return;
        return await api.exec(v2.templates.create({
            label: draft.label,
            folder: draft.group,
            board: source.id,
        }));
    }, [source, draft, draftValidate]);

    const onAction = useCallback(async (confirm?: boolean) => {
        if (loading || working) return;
        if (confirm) {
            const board = await onPublish();
            if (board) onComplete(board);
        }
        else {
            onComplete(null);
        }
    }, [loading, working, onPublish, onComplete]);

    const promptRef = usePrompt({ enterRequiresCtrl: true, onAction, });

    return <LoadingGuard loading={loading}>
        <Frame ref={promptRef}>
            <Input
                label="Name:"
                value={draft.label}
                error={errors.label}
                onChange={x => patchDraft({ label: x })} />
            <Flexer>
                <Input.Label>Folder:</Input.Label>
                <Select error={errors.group} items={templates ?? []} onChange={x => patchDraft({ group: x.id })} renderItems={
                    (items, selected, onSelect) => items.map((tm, i) =>
                        <Select.MenuItem key={tm.id} onClick={onSelect(tm) as any}>
                            <Flexer grow flex="row" align="center" gap=".5">
                                <Icon size="1.5" src={tm.icon ?? 'deqo://icons/dash/folder-list-icon'} />
                                <Copy flush>{tm.label}</Copy>
                            </Flexer>
                            {tm.id === selected?.id && <Select.MenuItemCheck />}
                        </Select.MenuItem>
                    )
                } />
            </Flexer>
            <ButtonStrip align="right">
                <Button variant="primary" onAction={() => onAction(true)}>Publish</Button>
                <Button variant="common" onAction={() => onAction(false)}>Cancel</Button>
            </ButtonStrip>
        </Frame>
    </LoadingGuard>;
};

export const PublishExistingView = ({ existing, onComplete }: { existing: TemplateData, onComplete: OnComplete }) => {

    const api = useResolve(ApiClient);
    const source = useEditorSessionObserver(BoardInfo);

    const [onApplyChanges, working] = useSubmitter(async () => {
        await api.exec(v2.templates.update({ templateId: existing.id, board: source.id }));
        return existing;
    }, [existing, source]);

    const onAction = useCallback(async (confirm?: boolean) => {
        if (working) return;
        if (confirm) {
            const board = await onApplyChanges();
            if (board) onComplete(board);
        }
        else {
            onComplete(null);
        }
    }, [working, onApplyChanges, onComplete]);

    const promptRef = usePrompt({ enterRequiresCtrl: true, onAction, });

    return <LoadingGuard loading={working}>
        <Frame ref={promptRef}>
            <Flexer flex="column" center gap="1">
                <Copy flush>
                    This board is linked to <a href={`/${existing.id}`} target="_blank" rel="noreferrer">an existing template</a>.
                    You can apply the board changes to this template, or if you want to create a new template, clone this board and publish that board to a new template.
                </Copy>
                <ButtonStrip align="right">
                    <Button variant="primary" disabled={working} onAction={() => onAction(true)}>Apply Changes</Button>
                    <Button variant="common" disabled={working} onAction={() => onAction(false)}>Cancel</Button>
                </ButtonStrip>
            </Flexer>
        </Frame>
    </LoadingGuard>;
};

export const publishPopup = () => ({ ui: PublishPopup });

export const PublishPopup = ({ action, dismiss }: PopupProps) => {

    const api = useResolve(ApiClient);
    const source = useEditorSessionObserver(BoardInfo);
    const [loading, existing] = useLoader(() => api.exec(v2.templates.findOne({ source: source.id })), [source]);

    const onComplete = useCallback((data?: BoardLiteData) => {
        if (data) {
            action(data);
        }
        else {
            dismiss();
        }
    }, [action, dismiss]);

    return <Popup>
        {!!loading && <Popup.Content>
            <Title size="lg" color={Colors.textLight} flush>Checking for existing template...</Title>
        </Popup.Content>}
        {!loading && <>
            <Popup.Close onAction={dismiss} />
            <Popup.Content>
                <Popup.Header>
                    <Popup.IconFrame>
                        <Icon src="deqo://icons/deqo/design-publish" size="2" />
                    </Popup.IconFrame>
                    <Popup.Title>
                        {!!existing ? 'Update Template' : 'Publish As Template'}
                    </Popup.Title>
                    <Popup.SubTitle>
                        {!!existing ? 'Apply the changes on your board to the linked template.' : 'Publish your board as a template to share with others.'}
                    </Popup.SubTitle>
                </Popup.Header>
                <Spacer f={1} />
                {!existing && <PublishNewView onComplete={onComplete} />}
                {!!existing && <PublishExistingView existing={existing} onComplete={onComplete} />}
            </Popup.Content>
        </>}
    </Popup>;
};
